import { DS2, DT } from '../../theme/designTokens';

/**
 * Settings V1 — shared DS2 gold toggle (Figma Settings "Music"/"Table Chat"
 * pill). The audit found no shared switch primitive (the old SettingsScreen
 * had a screen-local one) — this is the reusable, token-driven replacement.
 *
 * Accessibility: a real <button role="switch" aria-checked> — Space/Enter
 * toggle natively, focus ring via :focus-visible outline. Controlled only;
 * the caller owns state and persistence.
 *
 * Figma anatomy: ON = gold gradient track with a dark gold-bordered knob on
 * the right; OFF = dark track with the same knob on the left. Sized down
 * ~0.72× from the 154×64 Figma reference to sit on a text row.
 */
export interface ToggleSwitchProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  /** Accessible name — required; the visible label lives outside the control. */
  ariaLabel: string;
  id?: string;
}

const TRACK_W = 78;
const TRACK_H = 34;
const KNOB    = 26;
const PAD     = 4;

export function ToggleSwitch({ checked, onChange, disabled = false, ariaLabel, id }: ToggleSwitchProps) {
  return (
    <button
      type="button"
      id={id}
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      style={{
        width:        TRACK_W,
        height:       TRACK_H,
        borderRadius: TRACK_H / 2,
        boxSizing:    'border-box',
        background:   checked ? DS2.gradient.gold : 'rgba(255,255,255,0.08)',
        border:       `1px solid ${DS2.color.gold}`,
        boxShadow:    checked ? '0 12px 32px -12px rgba(201,168,76,0.45)' : 'none',
        position:     'relative',
        flexShrink:   0,
        cursor:       disabled ? 'not-allowed' : 'pointer',
        opacity:      disabled ? 0.45 : 1,
        padding:      0,
        transition:   `background ${DT.motion.fast}ms, box-shadow ${DT.motion.fast}ms`,
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          position:     'absolute',
          top:          PAD - 1,
          left:         checked ? TRACK_W - KNOB - PAD - 1 : PAD - 1,
          width:        KNOB,
          height:       KNOB,
          borderRadius: '50%',
          boxSizing:    'border-box',
          background:   '#131313',
          border:       `1px solid ${DS2.color.gold}`,
          transition:   `left ${DT.motion.fast}ms`,
        }}
      />
    </button>
  );
}
